'use client';

import { c as _c } from "react/compiler-runtime";
import { CartCountDocument } from '@data-access/lib/gql/cart/documents';
import { getGraphqlQueryKey } from '@data-access/lib/graphql-query';
import { refreshCartCurrency } from '@feat/feat-cart/currency';
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/navigation';
import { type ReactNode, startTransition, useEffectEvent, useRef, useState } from 'react';
import { clientEnv } from '@/env/client';
import { reportTerminalDayPriceError } from './_lib/report-terminal-day-price-error';
const ReactQueryDevtools = dynamic(() => import('@tanstack/react-query-devtools').then(mod => mod.ReactQueryDevtools), {
  ssr: false
});

// Cart mutations refresh the header count and the product-page cart currency through the
// query cache, not router.refresh(): that refresh scrolled the page to the footer (X-30).
// Here and not per mutation call: per-call callbacks are skipped once the caller unmounted.
const CART_MUTATION_KEYS = new Set(['cartItemAccommodationUpsert', 'cartItemAccommodationsCreate', 'cartItemBeachChairInsert', 'cartItemDelete', 'cartItemExperienceUpsert', 'cartItemRentableUpsert']);
const HEADER_REFRESH_MUTATION_KEYS = new Set(['addItemToWishlist', 'createWishlist', 'deleteWishlist', 'removeItemFromWishlist', 'renameWishlist', 'switchWishlists']);
export function AppApiProvider(t0) {
  const $ = _c(8);
  const {
    children
  } = t0;
  const router = useRouter();
  const refreshQueuedRef = useRef(false);
  let t1;
  if ($[0] !== router) {
    t1 = () => {
      if (refreshQueuedRef.current) {
        return;
      }
      refreshQueuedRef.current = true;
      queueMicrotask(() => {
        refreshQueuedRef.current = false;
        startTransition(() => {
          router.refresh();
        });
      });
    };
    $[0] = router;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const refreshHeader = useEffectEvent(t1);
  let t2;
  if ($[2] !== refreshHeader) {
    t2 = () => createQueryClient(refreshHeader);
    $[2] = refreshHeader;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const [queryClient] = useState(t2);
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = clientEnv.NEXT_PUBLIC_SHOW_REACT_QUERY_DEV_TOOLS && <ReactQueryDevtools initialIsOpen={false} />;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== children || $[6] !== queryClient) {
    t4 = <QueryClientProvider client={queryClient}>{children}{t3}</QueryClientProvider>;
    $[5] = children;
    $[6] = queryClient;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  return t4;
}
function createQueryClient(refreshHeader: VoidFunction) {
  const queryClient: QueryClient = new QueryClient({
    queryCache: new QueryCache({
      onError: (error, query) => reportTerminalDayPriceError(error, query, {
        environment: clientEnv.NEXT_PUBLIC_APP_ENV,
        release: clientEnv.NEXT_PUBLIC_SHOP_RELEASE ?? 'local-unversioned'
      })
    }),
    mutationCache: new MutationCache({
      onSuccess: (_data, _variables, _context, mutation) => {
        if (isMutationKeyIn(CART_MUTATION_KEYS, mutation.options.mutationKey)) {
          void queryClient.invalidateQueries({
            queryKey: getGraphqlQueryKey(CartCountDocument)
          });
          refreshCartCurrency(queryClient);
        }
        if (!isMutationKeyIn(HEADER_REFRESH_MUTATION_KEYS, mutation.options.mutationKey)) {
          return;
        }
        refreshHeader();
      }
    }),
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false
      }
    }
  });
  return queryClient;
}
function isMutationKeyIn(keys: Set<string>, mutationKey: readonly unknown[] | undefined) {
  const key = mutationKey?.[0];
  return typeof key === 'string' && keys.has(key);
}